import {
  COMMUNITY_ENFORCEMENT,
  COMMUNITY_LICENSE_URL,
  COMMUNITY_WARNING,
  BLUEPRINT_POLICY_VERSION,
} from './communityPolicy';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  blueprintSourceExtension,
  getBlueprintFileError,
  MAX_BLUEPRINT_FILE_SIZE,
} from '@/common/utils/blueprintFilePolicy';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  FormControlLabel,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Link,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useModeTokens } from '@/common/components/ui/useModeTokens';
import { AudioPlayer } from '@/common/components/projectDetails/components/AudioPlayer';
import { BlueprintArtwork } from './BlueprintArtwork';
import { BlueprintAudioPreview } from './BlueprintAudioPreview';
import { useLocalBlueprintMedia } from './useLocalBlueprintMedia';
import { BlueprintDetectionPanel } from './BlueprintDetectionPanel';
import {
  useBlueprintDetection,
  withDetectedRequirements,
  withoutDetectedRequirements,
} from './useBlueprintDetection';
import './blueprints.css';
import {
  useBlueprintUploadPrepareMutation,
  useBlueprintVersionPushMutation,
  useBlueprintVersionFinalizeMutation,
  useBlueprintUpdateMutation,
  type BlueprintFieldsFragment,
  type BlueprintMetadataInput,
} from '@/generated/graphql';
import { calculateHashes } from '@/common/utils/fileUtils';
import {
  uploadFileToS3,
  throwIfUploadAborted,
} from '@/common/utils/uploadUtils';
import { useTransferManagerContext } from '@/context/TransferManagerContext';
import {
  serializeBlueprintChangelog,
  stripBlueprintBulletPrefix,
} from './blueprintChangelog';
const empty: BlueprintMetadataInput = {
  blueprintType: 'fxChain',
  pluginName: '',
  pluginVersion: '',
  title: '',
  description: '',
  tags: [],
  dawVersion: '',
  requirements: '',
  stockOnly: false,
};
export function BlueprintUploadForm({
  onClose,
  onSaved,
  edit,
  newVersion = false,
  tags,
  creatorName,
}: {
  onClose: () => void;
  onSaved: (blueprintId: string, version?: number) => void;
  edit?: BlueprintFieldsFragment;
  newVersion?: boolean;
  tags: string[];
  creatorName: string;
}) {
  const t = useModeTokens();
  const editingDetails = !!edit && !newVersion;
  const [changeItems, setChangeItems] = useState(['']);
  const changeInputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const commitMessage = serializeBlueprintChangelog(changeItems);
  const [previewExpanded, setPreviewExpanded] = useState(true);
  const desktopPreview = useMediaQuery(useTheme().breakpoints.up('lg'));
  const mounted = useRef(true);
  const [metadata, setMetadata] = useState<BlueprintMetadataInput>(() =>
    edit
      ? {
          blueprintType: edit.blueprintType,
          pluginName: edit.pluginName,
          pluginVersion: edit.pluginVersion,
          title: edit.title,
          description: edit.description,
          tags: edit.tags,
          dawVersion: edit.dawVersion,
          requirements: newVersion
            ? withoutDetectedRequirements(edit.requirements)
            : edit.requirements,
          stockOnly: edit.stockOnly,
        }
      : empty
  );
  const isPreset = metadata.blueprintType === 'preset';
  const [blueprint, setBlueprint] = useState<File>();
  const detection = useBlueprintDetection(blueprint);
  const touchedCompatibility = useRef(new Set<string>());
  const detectedMetadata = useRef<{ name?: string }>({});
  const requirements = withDetectedRequirements(
    metadata.requirements,
    detection.selected
  );
  useEffect(() => {
    if (!blueprint) return;
    const previousName = detectedMetadata.current.name;
    // Retire only values inserted by a previous scan. Handwritten values stay intact.
    setMetadata((current) => ({
      ...current,
      pluginName:
        !touchedCompatibility.current.has('pluginName') &&
        current.pluginName === previousName
          ? ''
          : current.pluginName,
      stockOnly: touchedCompatibility.current.has('stockOnly')
        ? current.stockOnly
        : false,
    }));
  }, [blueprint]);
  useEffect(() => {
    const result = detection.result;
    if (!result) return;
    setMetadata((current) => {
      const name =
        isPreset &&
        result.pluginName &&
        !touchedCompatibility.current.has('pluginName')
          ? result.pluginName
          : current.pluginName;
      return {
        ...current,
        pluginName: name,
        pluginVersion:
          name !== current.pluginName &&
          !touchedCompatibility.current.has('pluginVersion')
            ? ''
            : current.pluginVersion,
        stockOnly:
          !isPreset &&
          (result.stockStatus === 'external'
            ? false
            : touchedCompatibility.current.has('stockOnly')
              ? current.stockOnly
              : result.stockStatus === 'stock'),
      };
    });
    detectedMetadata.current = { name: result.pluginName };
  }, [detection.result, isPreset]);
  const [thumbnail, setThumbnail] = useState<File>();
  const [preview, setPreview] = useState<File>();
  const media = useLocalBlueprintMedia(thumbnail, preview);
  const [rights, setRights] = useState(false);
  const [license, setLicense] = useState(false);
  const [busy, setBusy] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const [error, setError] = useState('');
  const [fileError, setFileError] = useState('');
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // The transfer manager owns submitted uploads, including after navigation.
    };
  }, []);
  const chooseFile = (kind: 'blueprint' | 'artwork' | 'audio', file?: File) => {
    if (!file || busy) return;
    if (kind === 'blueprint') {
      const reason = getBlueprintFileError(file.name, file.type, file.size);
      if (reason) {
        setFileError(reason);
        return;
      }
      if (newVersion && edit) {
        const originalExtension = blueprintSourceExtension(
          edit.fileName
        ).toLowerCase();
        const nextExtension = blueprintSourceExtension(file.name).toLowerCase();
        if (originalExtension !== nextExtension) {
          setFileError(
            originalExtension
              ? `New versions must use the original ${originalExtension} file type.`
              : 'New versions of this blueprint must remain extensionless.'
          );
          return;
        }
      }
    }
    const rule = {
      blueprint: {
        extension: null,
        size: MAX_BLUEPRINT_FILE_SIZE / 1_000_000,
        label: 'a preset or FX chain file',
      },
      artwork: {
        extension: /\.(png|jpe?g|webp)$/i,
        size: 5,
        label: 'PNG, JPG, or WebP artwork',
      },
      audio: {
        extension: /\.(mp3|wav)$/i,
        size: 20,
        label: 'an MP3 or WAV preview',
      },
    }[kind];
    if (
      (rule.extension && !rule.extension.test(file.name)) ||
      file.size === 0 ||
      file.size > rule.size * 1000000
    ) {
      setFileError(
        `Choose ${rule.label}, up to ${rule.size} MB. Empty files are not supported.`
      );
      return;
    }
    setFileError('');
    if (kind === 'blueprint') {
      setBlueprint(file);
      if (!edit && !newVersion) {
        setMetadata((current) =>
          current.title.trim()
            ? current
            : {
                ...current,
                title: file.name
                  .replace(/(?!^)\.[^.]+$/, '')
                  .trim()
                  .slice(0, 100),
              }
        );
      }
    } else if (kind === 'artwork') setThumbnail(file);
    else setPreview(file);
  };
  const [prepare] = useBlueprintUploadPrepareMutation();
  const [pushVersion] = useBlueprintVersionPushMutation();
  const [finalize] = useBlueprintVersionFinalizeMutation();
  const [update] = useBlueprintUpdateMutation();
  const { enqueueCreate } = useTransferManagerContext();
  const field = (key: keyof BlueprintMetadataInput, value: unknown) => {
    touchedCompatibility.current.add(key);
    setMetadata((m) => ({ ...m, [key]: value }));
  };
  const updateChange = (index: number, value: string) => {
    setChangeItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? stripBlueprintBulletPrefix(value) : item
      )
    );
  };
  const addChangeAfter = (index: number) => {
    if (changeItems.length >= 20) return;
    setChangeItems((current) => [
      ...current.slice(0, index + 1),
      '',
      ...current.slice(index + 1),
    ]);
    window.requestAnimationFrame(() =>
      changeInputRefs.current[index + 1]?.focus()
    );
  };
  const removeChange = (index: number) => {
    if (changeItems.length === 1) return;
    setChangeItems((current) =>
      current.filter((_, itemIndex) => itemIndex !== index)
    );
    window.requestAnimationFrame(() =>
      changeInputRefs.current[Math.max(0, index - 1)]?.focus()
    );
  };
  const submit = async () => {
    if (newVersion && detection.status === 'checking') {
      setError('Wait for device detection to finish before publishing.');
      return;
    }
    if (
      !rights ||
      !license ||
      abortRef.current ||
      requirements.length > 2000 ||
      commitMessage.length > 5000
    )
      return;
    detection.cancel();
    const uploadMetadata = { ...metadata, requirements };
    const consent = {
      policyVersion: BLUEPRINT_POLICY_VERSION,
      rightsConfirmed: rights,
      licenseConfirmed: license,
    };
    const controller = new AbortController();
    abortRef.current = controller;
    let detach = () => {};
    let savedId = edit?._id;
    let savedVersion: number | undefined;
    let scanPending = false;
    setBusy(true);
    setError('');
    try {
      if (editingDetails && edit) {
        await update({
          variables: { id: edit._id, metadata: uploadMetadata, consent },
        });
      } else {
        if (!blueprint || !preview)
          throw new Error('Choose a blueprint file and audio preview.');
        if (newVersion && !commitMessage)
          throw new Error('Add at least one change for this version.');
        const assets = [
          { kind: 'blueprint', file: blueprint },
          ...(thumbnail ? [{ kind: 'thumbnail', file: thumbnail }] : []),
          ...(preview ? [{ kind: 'preview', file: preview }] : []),
        ];
        let finished = false;
        await enqueueCreate({
          subject: {
            kind: 'blueprint',
            id: edit?._id || crypto.randomUUID(),
            label: metadata.title,
          },
          statusMessage: 'Uploading Blueprint',
          files: assets.map(({ file }) => ({
            fileName: file.name,
            size: file.size,
          })),
          successMessage:
            'Upload received. It will appear after the security scan passes.',
          run: async (callbacks) => {
            const queueSignal = callbacks.getAbortSignal?.();
            const cancel = () => controller.abort();
            queueSignal?.addEventListener('abort', cancel, { once: true });
            detach = () => queueSignal?.removeEventListener('abort', cancel);
            if (queueSignal?.aborted) cancel();
            throwIfUploadAborted(controller.signal);
            const files = await Promise.all(
              assets.map(async ({ kind, file }) => ({
                kind,
                ...(kind === 'preview'
                  ? { waveForm: await media.getWaveform(file) }
                  : {}),
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
                size: file.size,
                fullHash: (await calculateHashes(file)).fullHash,
              }))
            );
            callbacks.onFilesPrepared?.(files);
            throwIfUploadAborted(controller.signal);
            const variables = {
              metadata: uploadMetadata,
              files,
              consent,
            };
            const draft =
              newVersion && edit
                ? (
                    await pushVersion({
                      variables: { ...variables, id: edit._id, commitMessage },
                    })
                  ).data?.blueprintVersionPush
                : (await prepare({ variables })).data?.blueprintUploadPrepare;
            if (!draft) throw new Error('Could not prepare the upload.');
            throwIfUploadAborted(controller.signal);
            savedId = draft.id;
            savedVersion = draft.version;
            callbacks.onContext?.({
              blueprintId: draft.id,
              versionId: draft.versionId,
              version: draft.version,
            });
            for (let i = 0; i < assets.length; i++) {
              const { kind, file } = assets[i];
              const url = draft.uploads.find((u) => u.kind === kind)!;
              callbacks.onFileStart(file.name);
              try {
                await uploadFileToS3(
                  file,
                  url.presignedUrl,
                  (p) => callbacks.onFileProgress?.(file.name, p),
                  controller.signal,
                  files[i].fullHash,
                  url.contentType
                );
                callbacks.onFileComplete(file.name);
              } catch (e) {
                callbacks.onFileError(file.name, String(e));
                throw e;
              }
            }
            throwIfUploadAborted(controller.signal);
            if (mounted.current) setPublishing(true);
            callbacks.onStatusMessage?.('Validating Blueprint');
            try {
              const result = await finalize({
                variables: { id: draft.id, versionId: draft.versionId },
              });
              const publication = result.data?.blueprintVersionFinalize;
              if (publication?.status === 'rejected')
                throw new Error(publication.message);
              scanPending = publication?.status === 'pendingScan';
              callbacks.onStatusMessage?.(
                scanPending
                  ? 'Security scan in progress'
                  : 'Blueprint published'
              );
            } catch (error) {
              callbacks.onDiagnostic?.('Validate and publish Blueprint', error);
              throw error;
            }
            window.dispatchEvent(new Event('blueprint-library-changed'));
            finished = true;
          },
        });
        if (!finished)
          throw new Error('Upload was cancelled or failed. Please try again.');
      }
      if (mounted.current && scanPending) onClose();
      else if (mounted.current && savedId) onSaved(savedId, savedVersion);
    } catch (e) {
      if (mounted.current)
        setError(
          e instanceof Error ? e.message : 'Could not publish this blueprint'
        );
    } finally {
      detach();
      abortRef.current = null;
      if (mounted.current) {
        setPublishing(false);
        setBusy(false);
      }
    }
  };
  const section = {
    pt: { xs: 3, sm: 4 },
    borderTop: `1px solid ${t.borders.hairline}`,
    minWidth: 0,
  };
  const canSave =
    (!newVersion || detection.status !== 'checking') &&
    requirements.length <= 2000 &&
    commitMessage.length <= 5000 &&
    rights &&
    license &&
    (editingDetails ||
      (blueprint && preview && (!newVersion || commitMessage))) &&
    metadata.title.trim() &&
    (isPreset
      ? metadata.pluginName?.trim() && metadata.pluginVersion?.trim()
      : metadata.dawVersion.trim()) &&
    metadata.tags.length;
  return (
    <Box
      sx={{
        '& .MuiInputBase-root': { minHeight: 44 },
        '& .MuiInputBase-input': { fontSize: { xs: 16, sm: 14 } },
        '& .MuiFormHelperText-root': { lineHeight: 1.6, mt: 0.75 },
        '& .MuiButton-root': { minHeight: { xs: 44, sm: 36 } },
      }}
    >
      {fileError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {fileError}
        </Alert>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: 'minmax(0, 1fr) minmax(300px, 0.65fr)',
          },
          gridTemplateAreas: {
            lg: '"details preview" "compatibility preview" "sharing preview"',
          },
          columnGap: { lg: 6, xl: 8 },
          rowGap: { xs: 3, sm: 4 },
          alignItems: 'start',
        }}
      >
        <Box
          component="section"
          aria-labelledby="blueprint-details-heading"
          sx={{ minWidth: 0, gridArea: { lg: 'details' } }}
        >
          <SectionHeading
            number="01"
            id="blueprint-details-heading"
            title="The sound"
            description="Give other producers a feel for what you made."
          />
          <Stack spacing={3}>
            <TextField
              select
              label="Blueprint type"
              value={metadata.blueprintType}
              disabled={busy || !!edit}
              onChange={(e) => {
                setMetadata((m) => ({
                  ...m,
                  blueprintType: e.target
                    .value as BlueprintMetadataInput['blueprintType'],
                  stockOnly: false,
                }));
                setBlueprint(undefined);
                setFileError('');
              }}
              helperText={
                edit
                  ? 'The type stays with the original file.'
                  : 'Share an effect chain or a preset for a plug-in.'
              }
              fullWidth
            >
              <MenuItem value="fxChain">FX chain</MenuItem>
              <MenuItem value="preset">Preset</MenuItem>
            </TextField>
            {newVersion && (
              <Alert severity="info">
                Upload the revised file and a fresh audio preview. To rename the
                blueprint or change its description and tags, use Edit details.
              </Alert>
            )}
            {newVersion && (
              <Box>
                <Typography variant="subtitle2">What changed?</Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, mb: 1.5 }}
                >
                  Add one clear change per bullet. Press Enter to add the next
                  one.
                </Typography>
                <Stack spacing={1.25}>
                  {changeItems.map((item, index) => (
                    <TextField
                      key={index}
                      label={`Change ${index + 1}`}
                      placeholder={
                        index === 0
                          ? 'e.g. Added a freeze macro'
                          : 'Describe another change'
                      }
                      value={item}
                      onChange={(event) =>
                        updateChange(index, event.target.value)
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && item.trim()) {
                          event.preventDefault();
                          addChangeAfter(index);
                        } else if (
                          event.key === 'Backspace' &&
                          !item &&
                          changeItems.length > 1
                        ) {
                          event.preventDefault();
                          removeChange(index);
                        }
                      }}
                      inputRef={(element) => {
                        changeInputRefs.current[index] = element;
                      }}
                      disabled={busy}
                      required={index === 0}
                      slotProps={{ htmlInput: { maxLength: 500 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              aria-hidden="true"
                              sx={{
                                width: 7,
                                height: 7,
                                borderRadius: '50%',
                                bgcolor: 'primary.main',
                              }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment:
                          changeItems.length > 1 ? (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={`Remove change ${index + 1}`}
                                size="small"
                                disabled={busy}
                                onClick={() => removeChange(index)}
                              >
                                <RemoveCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : undefined,
                      }}
                      fullWidth
                    />
                  ))}
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                  sx={{ mt: 1.5 }}
                >
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={busy || changeItems.length >= 20}
                    onClick={() => addChangeAfter(changeItems.length - 1)}
                  >
                    Add change
                  </Button>
                  <Typography
                    variant="caption"
                    color={
                      commitMessage.length > 5000
                        ? 'error.main'
                        : 'text.secondary'
                    }
                  >
                    {commitMessage.length.toLocaleString()} / 5,000
                  </Typography>
                </Stack>
              </Box>
            )}
            {!editingDetails && (
              <Box
                className="blueprint-upload-zone"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  chooseFile('blueprint', e.dataTransfer.files[0]);
                }}
                sx={{
                  borderColor: blueprint
                    ? t.colors.accentText
                    : t.borders.hairline,
                  background: t.surfaces.fillFaint,
                  py: { xs: 2.5, sm: 3 },
                  borderRadius: '10px',
                  textAlign: 'center',
                  transition:
                    'border-color 160ms ease, background-color 160ms ease',
                  '&:hover, &:focus-within': busy
                    ? {}
                    : {
                        borderColor: t.colors.accentText,
                        background: t.surfaces.fillSubtle,
                      },
                  '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                  },
                }}
              >
                {blueprint ? (
                  <CheckCircleOutlineIcon
                    color="primary"
                    sx={{ fontSize: 30 }}
                  />
                ) : (
                  <CloudUploadOutlinedIcon
                    sx={{ fontSize: 30, color: 'text.secondary' }}
                  />
                )}
                <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
                  {blueprint?.name ||
                    (isPreset
                      ? 'Drop your preset file here'
                      : 'Drop your FX chain file here')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Preset or FX chain · up to 10 MB
                </Typography>
                <Button
                  component="label"
                  size="small"
                  variant="outlined"
                  disabled={busy}
                >
                  {blueprint ? 'Replace blueprint file' : 'Browse files'}
                  <input
                    hidden
                    aria-label="Blueprint file"
                    type="file"
                    disabled={busy}
                    onChange={(e) => {
                      chooseFile('blueprint', e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Box>
            )}
            <TextField
              label="Blueprint name"
              placeholder="e.g. Reverb Throw"
              value={metadata.title}
              onChange={(e) => field('title', e.target.value)}
              inputProps={{ maxLength: 100 }}
              required
              disabled={busy || newVersion}
              fullWidth
            />
            <TextField
              label="What does it do?"
              placeholder="Describe the sound, where to use it, and what the macros control…"
              multiline
              minRows={3}
              value={metadata.description}
              onChange={(e) => field('description', e.target.value)}
              inputProps={{ maxLength: 5000 }}
              disabled={busy || newVersion}
              fullWidth
            />
            <Autocomplete
              multiple
              freeSolo
              options={tags}
              value={metadata.tags}
              onChange={(_, value) => field('tags', value.slice(0, 12))}
              disabled={busy || newVersion}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tags"
                  placeholder={
                    metadata.tags.length ? '' : 'e.g. bass, ambient, glitch'
                  }
                  helperText="Press Enter to add a tag. Choose up to 12."
                />
              )}
            />
          </Stack>
        </Box>
        <Box
          component="aside"
          aria-label="Blueprint preview"
          sx={{
            minWidth: 0,
            border: `1px solid ${t.borders.hairline}`,
            borderRadius: '12px',
            background: t.surfaces.fillFaint,
            position: { lg: 'sticky' },
            top: 80,
            alignSelf: 'start',
            gridArea: { lg: 'preview' },
            maxHeight: { lg: 'calc(100dvh - 104px)' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{
              px: { xs: 2.5, sm: 3.5 },
              py: 2,
              display: { xs: 'none', lg: 'flex' },
            }}
          >
            <Box>
              <Typography component="h2" variant="body2" fontWeight={600}>
                Your blueprint
              </Typography>
              <Typography variant="caption" color="text.secondary">
                A little preview of what you’re sharing
              </Typography>
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              sx={{ color: t.colors.accentText, flexShrink: 0 }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  bgcolor: 'currentColor',
                }}
              />
              <Typography variant="caption">Live</Typography>
            </Stack>
          </Stack>
          <ButtonBase
            aria-expanded={previewExpanded}
            aria-controls="blueprint-preview-content"
            onClick={() => setPreviewExpanded((expanded) => !expanded)}
            sx={{
              display: { xs: 'flex', lg: 'none' },
              width: '100%',
              p: 2,
              gap: 1.5,
              textAlign: 'left',
              justifyContent: 'space-between',
              borderRadius: '12px',
              '&.Mui-focusVisible': {
                outline: `2px solid ${t.colors.accentText}`,
                outlineOffset: -3,
              },
            }}
          >
            <ImageOutlinedIcon
              sx={{ color: t.colors.accentText, fontSize: 22 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {editingDetails ? 'Preview your blueprint' : 'Artwork & audio'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editingDetails
                  ? 'View your cover and audio preview'
                  : thumbnail || preview
                    ? [thumbnail && 'Artwork added', preview && 'Audio added']
                        .filter(Boolean)
                        .join(' · ')
                    : 'Audio required · Artwork optional'}
              </Typography>
            </Box>
            <ExpandMoreIcon
              sx={{ transform: previewExpanded ? 'rotate(180deg)' : 'none' }}
            />
          </ButtonBase>
          {(desktopPreview || previewExpanded) && (
            <Box
              id="blueprint-preview-content"
              sx={{
                p: 1.5,
                pt: { xs: 0, lg: 0.5 },
                minHeight: 0,
                overflowY: { lg: 'auto' },
                overscrollBehaviorY: 'contain',
                scrollbarWidth: 'thin',
                '& .blueprint-art': {
                  height: { xs: 170, sm: 230, lg: 'clamp(150px, 22vh, 210px)' },
                },
              }}
            >
              <BlueprintArtwork
                title={metadata.title || 'Your next signature sound'}
                image={media.artworkUrl || edit?.thumbnailUrl}
                large
              />
              <Box sx={{ px: { xs: 1, sm: 1.5 }, pt: 2, pb: 1.5 }}>
                {!editingDetails && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 2, flexWrap: 'wrap' }}
                  >
                    <Button
                      component="label"
                      size="small"
                      startIcon={<ImageOutlinedIcon />}
                      disabled={busy}
                    >
                      {thumbnail ? 'Change artwork' : 'Add artwork'}
                      <input
                        hidden
                        aria-label="Artwork file"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        disabled={busy}
                        onChange={(e) => {
                          chooseFile('artwork', e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                    {thumbnail ? (
                      <IconButton
                        size="small"
                        aria-label="Remove artwork"
                        sx={{
                          minWidth: { xs: 44, sm: 30 },
                          minHeight: { xs: 44, sm: 30 },
                        }}
                        disabled={busy}
                        onClick={() => setThumbnail(undefined)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        PNG, JPG, WebP · 5 MB
                      </Typography>
                    )}
                  </Stack>
                )}
                {(isPreset ? metadata.pluginName : metadata.dawVersion) && (
                  <Typography variant="caption" color="text.secondary">
                    {isPreset
                      ? `${metadata.pluginName} ${metadata.pluginVersion || ''} · PRESET`
                      : `ABLETON LIVE ${metadata.dawVersion} · FX CHAIN`}
                    {metadata.stockOnly ? ' · STOCK DEVICES' : ''}
                  </Typography>
                )}
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{
                    mt: 0.5,
                    mb: 0.5,
                    fontSize: { xs: 20, sm: 22 },
                    fontWeight: 600,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {metadata.title.trim() || 'Your blueprint name'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  by{' '}
                  <Box
                    component="span"
                    sx={{ color: t.colors.accentText, fontWeight: 600 }}
                  >
                    {edit?.creatorName || creatorName}
                  </Box>
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    my: 2,
                    overflowWrap: 'anywhere',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {metadata.description.trim() ||
                    'Your description will appear here. Give people a feel for the sound.'}
                </Typography>
                {!!metadata.tags.length && (
                  <Stack
                    direction="row"
                    gap={0.75}
                    flexWrap="wrap"
                    sx={{ mb: 2 }}
                  >
                    {metadata.tags.map((tag) => (
                      <Chip
                        key={tag}
                        size="small"
                        label={tag}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
                <Divider sx={{ mb: 2 }} />
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1.5 }}
                >
                  <GraphicEqIcon
                    sx={{ fontSize: 18, color: 'text.secondary' }}
                  />
                  <Typography variant="body2" fontWeight={600}>
                    Hear it in action
                  </Typography>
                </Stack>
                {preview && media.audioUrl ? (
                  <Box>
                    {media.decoding ? (
                      <LinearProgress
                        aria-label="Preparing audio waveform"
                        sx={{ my: 2 }}
                      />
                    ) : (
                      <AudioPlayer
                        key={media.audioUrl}
                        waveForm={media.waveForm}
                        fileId="blueprint-local-preview"
                        label={`${metadata.title || 'Blueprint'} preview`}
                        resolveAudioSource={async () => media.audioUrl || null}
                      />
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1, overflowWrap: 'anywhere' }}
                    >
                      {preview.name}
                    </Typography>
                  </Box>
                ) : editingDetails && edit?.previewUrl ? (
                  <BlueprintAudioPreview blueprint={edit} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {editingDetails
                      ? 'No audio preview attached.'
                      : 'Let people hear what your blueprint can do.'}
                  </Typography>
                )}
                {!editingDetails && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 1.5, flexWrap: 'wrap' }}
                  >
                    <Button
                      component="label"
                      size="small"
                      variant="outlined"
                      disabled={busy}
                    >
                      {preview ? 'Replace audio' : 'Add audio preview'}
                      <input
                        hidden
                        aria-label="Audio preview file"
                        type="file"
                        accept=".mp3,.wav"
                        disabled={busy}
                        onChange={(e) => {
                          chooseFile('audio', e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </Button>
                    {preview && (
                      <IconButton
                        size="small"
                        aria-label="Remove audio preview"
                        sx={{
                          minWidth: { xs: 44, sm: 30 },
                          minHeight: { xs: 44, sm: 30 },
                        }}
                        disabled={busy}
                        onClick={() => setPreview(undefined)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                )}
                {!editingDetails && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1.5 }}
                  >
                    {preview
                      ? 'Preview is local until you publish.'
                      : 'Required for this version · MP3 or WAV · up to 20 MB'}
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  px: { xs: 1, sm: 1.5 },
                  pt: 1.5,
                  pb: 0.5,
                  borderTop: `1px solid ${t.borders.hairline}`,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.6 }}
                >
                  {editingDetails
                    ? 'Editing details only. Your original files stay attached.'
                    : newVersion
                      ? 'Each release has its own file, changelog, and audio. Your cover carries over unless you replace it.'
                      : 'No artwork? We’ll give your blueprint a Studio Vault cover.'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        <Box
          component="section"
          aria-labelledby="blueprint-compatibility-heading"
          sx={{ ...section, gridArea: { lg: 'compatibility' } }}
        >
          <SectionHeading
            number="02"
            id="blueprint-compatibility-heading"
            title="Made to work together"
            description="Help people know what they need before downloading."
          />
          <Stack spacing={2.5}>
            {!editingDetails && (
              <BlueprintDetectionPanel
                detection={detection}
                disabled={busy}
                isPreset={isPreset}
                onUsePlugin={(name) => {
                  field('pluginName', name);
                  if (name !== metadata.pluginName) field('pluginVersion', '');
                }}
                onUseVersion={(version) =>
                  field(isPreset ? 'pluginVersion' : 'dawVersion', version)
                }
              />
            )}
            {isPreset && (
              <TextField
                label="Plug-in name"
                placeholder="e.g. Serum"
                value={metadata.pluginName}
                onChange={(e) => field('pluginName', e.target.value)}
                inputProps={{ maxLength: 100 }}
                required
                disabled={busy || editingDetails}
                fullWidth
              />
            )}
            <TextField
              label={
                isPreset
                  ? 'Required plug-in version'
                  : 'Required Ableton Live version'
              }
              placeholder={isPreset ? 'e.g. 2.0' : 'e.g. 12.1'}
              value={isPreset ? metadata.pluginVersion : metadata.dawVersion}
              onChange={(e) =>
                field(isPreset ? 'pluginVersion' : 'dawVersion', e.target.value)
              }
              inputProps={{ maxLength: 40 }}
              required
              disabled={busy || editingDetails}
              fullWidth
            />
            {!isPreset && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={metadata.stockOnly}
                    onChange={(e) => field('stockOnly', e.target.checked)}
                    disabled={
                      busy ||
                      editingDetails ||
                      detection.result?.stockStatus === 'external'
                    }
                  />
                }
                label="Uses only Ableton stock devices"
              />
            )}
            <TextField
              label="Devices, edition, and other requirements"
              placeholder={
                isPreset
                  ? 'List required plug-ins, sample libraries, or wavetables. Include only material you can share.'
                  : 'e.g. Live Suite; no external samples. List any required plug-ins.'
              }
              multiline
              minRows={3}
              value={metadata.requirements}
              onChange={(e) => field('requirements', e.target.value)}
              error={requirements.length > 2000}
              helperText={
                requirements.length > 2000
                  ? 'Selected devices and notes must fit within 2,000 characters. Remove a device or shorten your notes.'
                  : detection.selected.length
                    ? 'Selected devices above are included automatically. Add editions, libraries, and any missing requirements here.'
                    : undefined
              }
              inputProps={{ maxLength: 2000 }}
              disabled={busy || editingDetails}
              fullWidth
            />
          </Stack>
        </Box>
        <Box
          component="section"
          aria-labelledby="blueprint-sharing-heading"
          sx={{ ...section, gridArea: { lg: 'sharing' } }}
        >
          <SectionHeading
            number="03"
            id="blueprint-sharing-heading"
            title="Made to be shared"
            description="An open source library of community-driven tools. A place to learn, build, and make something new together."
          />
          <Stack spacing={2.5}>
            <Box
              sx={{
                pl: 2,
                py: 0.5,
                borderLeft: `2px solid ${t.colors.amber}`,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {COMMUNITY_WARNING}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {COMMUNITY_ENFORCEMENT}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Original work made with paid software is welcome. Do not include
                purchased or factory content, or material you cannot release
                under CC0.
              </Typography>
            </Box>
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
                mx: 0,
                '& .MuiCheckbox-root': { pt: 0, pl: 0 },
              }}
              control={
                <Checkbox
                  checked={rights}
                  onChange={(e) => setRights(e.target.checked)}
                  disabled={busy}
                />
              }
              label={
                <Typography variant="body2">
                  I created and control this contribution, or have verified its
                  source is public domain or CC0. I have the rights to share the
                  blueprint and every attachment, and have not included
                  purchased or unauthorized content.
                </Typography>
              }
            />
            <FormControlLabel
              sx={{
                alignItems: 'flex-start',
                mx: 0,
                '& .MuiCheckbox-root': { pt: 0, pl: 0 },
              }}
              control={
                <Checkbox
                  checked={license}
                  onChange={(e) => setLicense(e.target.checked)}
                  disabled={busy}
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  <Link
                    href="/community-guidelines"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Community guidelines
                  </Link>{' '}
                  and irrevocably apply{' '}
                  <Link
                    href={COMMUNITY_LICENSE_URL}
                    target="_blank"
                    rel="noreferrer"
                  >
                    CC0 1.0
                  </Link>{' '}
                  to my contribution and attachments, to the extent permitted by
                  law. Anyone can copy, modify, redistribute, or sell them
                  without permission, payment, or required credit. Deleting this
                  upload does not undo that dedication.
                </Typography>
              }
            />
          </Stack>
        </Box>
      </Box>
      <Box
        sx={{
          mt: { xs: 4, sm: 5 },
          borderTop: `1px solid ${t.borders.hairline}`,
          pt: 3,
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {busy && (
          <LinearProgress aria-label="Uploading blueprint" sx={{ mb: 2 }} />
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
        >
          <Typography variant="body2" color="text.secondary">
            {busy
              ? publishing
                ? 'Publishing to the community library…'
                : 'Your upload will continue if you leave this page.'
              : editingDetails
                ? 'Your changes will appear in the community library.'
                : 'Ready to pass it on? Check your details and sharing permissions.'}
          </Typography>
          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={1.5}
            justifyContent="flex-end"
            sx={{
              flexShrink: 0,
              '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' } },
            }}
          >
            <Button
              onClick={busy ? () => abortRef.current?.abort() : onClose}
              disabled={publishing || (busy && editingDetails)}
            >
              {busy ? 'Cancel upload' : 'Cancel'}
            </Button>
            <Button
              variant="contained"
              onClick={() => void submit()}
              disabled={busy || !canSave}
            >
              {busy
                ? 'Saving…'
                : editingDetails
                  ? 'Save changes'
                  : newVersion
                    ? `Publish v${(edit?.version || 1) + 1}`
                    : 'Publish blueprint'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

function SectionHeading({
  number,
  id,
  title,
  description,
}: {
  number: string;
  id: string;
  title: string;
  description: ReactNode;
}) {
  const t = useModeTokens();
  return (
    <Stack direction="row" spacing={1.75} sx={{ mb: 3 }}>
      <Box
        sx={{
          width: 28,
          height: 32,
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          fontSize: 12,
          fontWeight: 500,
          fontFamily: 'monospace',
          color: t.colors.accentText,
          borderBottom: `2px solid ${t.colors.accentText}`,
        }}
      >
        {number}
      </Box>
      <Box>
        <Typography
          component="h2"
          id={id}
          variant="h6"
          sx={{
            fontWeight: 600,
            fontSize: { xs: 18, sm: 20 },
            lineHeight: 1.5,
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}
